import { Injectable, inject } from '@angular/core';

import { IQPort, Q_PORT } from './q.port';
import { ITimeoutPort, ITimeoutPromise, TIMEOUT_PORT } from './timeout.port';

/**
 * UtilsService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/helpers/utils.service.js
 *
 * Exposed to AngularJS via `downgradeInjectable` under the legacy name
 * `'UtilsService'` (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class UtilsService {
  private $timeout = inject<ITimeoutPort>(TIMEOUT_PORT);
  private $q = inject<IQPort>(Q_PORT);

  private timestampOfEventFired: number | undefined;

  clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  removeUndefinedProperties<T extends Record<string, unknown>>(
    data: T | null | undefined,
    isOnChanges?: boolean,
  ): Record<string, unknown> {
    const _data: Record<string, unknown> = (data ?? {}) as Record<string, unknown>;

    return Object.keys(_data).reduce<Record<string, unknown>>((result, key) => {
      let value: unknown = _data[key];

      if (isOnChanges) {
        value = (_data[key] as { currentValue: unknown }).currentValue;
      }

      if (typeof value !== 'undefined') {
        result[key] = value;
      }

      return result;
    }, {});
  }

  unwrapAcmeResponse<T = unknown>(response: { data: { payload: T } }): T {
    return response.data.payload;
  }

  zip<T = unknown>(...arrs: T[][]): T[][] {
    const resultLength = Math.min(...arrs.map((item) => item.length));

    return Array.from({ length: resultLength }, (_, i) => arrs.map((item) => item[i]));
  }

  debounce<TArgs extends unknown[], TResult>(
    func: (...args: TArgs) => TResult,
    wait: number,
    immediate?: boolean,
  ): (...args: TArgs) => Promise<TResult> {
    const $timeout = this.$timeout;
    const $q = this.$q;
    let timeout: ITimeoutPromise | undefined;
    let deferred = $q.defer<TResult>();

    return function (this: unknown, ...args: TArgs): Promise<TResult> {
      const context = this;
      const later = () => {
        timeout = undefined;
        if (!immediate) {
          deferred.resolve(func.apply(context, args) as TResult);
          deferred = $q.defer<TResult>();
        }
      };
      const callNow = immediate && !timeout;

      if (timeout) {
        $timeout.cancel(timeout);
      }

      timeout = $timeout(later, wait);

      if (callNow) {
        deferred.resolve(func.apply(context, args) as TResult);
        deferred = $q.defer<TResult>();
      }

      return deferred.promise;
    };
  }

  getDateIfValid(possibleDate: string | null | undefined): Date | undefined {
    if (possibleDate) {
      const date = Date.parse(possibleDate);

      if (!isNaN(date)) {
        return new Date(possibleDate);
      }
    }
    return undefined;
  }

  /**
   * Workaround to prevent an ng-click event firing multiple times when AG Grid
   * + DataGrid component are used together. Preserves the timestamp on the
   * service singleton so consecutive identical timestamps short-circuit.
   */
  eventAlreadyFired($event: { timeStamp: number }): boolean {
    if ($event.timeStamp === this.timestampOfEventFired) {
      return true;
    }
    this.timestampOfEventFired = $event.timeStamp;
    return false;
  }
}
